'use client';

import React from 'react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';

const disclaimerItems = [
  {
    icon: '◈',
    title: 'Loan Approval Is Not Guaranteed',
    content: `Submission of a loan application through the Lenders Home LLC platform does not constitute a guarantee of loan approval. All applications are subject to review and approval by lending program partners based on their individual underwriting criteria. Lenders Home LLC does not make final approval decisions and cannot guarantee that any applicant will be approved for any specific loan amount, rate, or term.

Approval decisions are based on a variety of factors including but not limited to credit history, income verification, employment status, debt-to-income ratio, and program-specific eligibility requirements. These criteria may change without notice.`,
  },
  {
    icon: '◆',
    title: 'Funding Timelines May Vary',
    content: `Estimated funding timelines provided during the application process are approximations only and are not guaranteed. Actual funding times depend on numerous factors including the completeness and accuracy of your application, the speed of document verification, the underwriting timeline of the lending program partner, banking system processing schedules, and any conditions that must be satisfied prior to disbursement.

Lenders Home LLC is not responsible for delays in funding caused by incomplete documentation, failed identity verification, banking system delays, federal holidays, or other factors outside our direct control. Funds are disbursed by the lending program partner, not by Lenders Home LLC.`,
  },
  {
    icon: '◉',
    title: 'Rates and Terms Vary by Applicant',
    content: `Interest rates, loan amounts, repayment terms, and fees presented during the application process are estimates only and are subject to change based on the final underwriting assessment. The actual rate and terms offered to any individual applicant may differ materially from initial estimates.

Rates are determined by lending program partners based on applicant creditworthiness, loan type, loan amount, repayment term, and other program-specific factors. Lenders Home LLC does not set or guarantee interest rates. Annual Percentage Rates (APR) will be clearly disclosed in your loan agreement prior to signing.`,
  },
  {
    icon: '◎',
    title: 'Credit and Income Verification May Be Required',
    content: `Most lending programs require verification of your credit history and income before a final approval decision can be made. This may include a hard credit inquiry, which can temporarily affect your credit score. You will be notified and asked for your consent before any hard credit pull is conducted.

Income verification may require submission of pay stubs, tax returns, bank statements, or other financial documentation. Failure to provide requested documentation in a timely manner may result in delays or denial of your application. All submitted documentation is subject to verification for accuracy and authenticity.`,
  },
  {
    icon: '◐',
    title: 'No Financial, Legal, or Tax Advice',
    content: `The information provided on the Lenders Home LLC platform, including but not limited to the Loan Guide, FAQ, How It Works pages, and any communications from our team, is for informational and educational purposes only. It does not constitute financial advice, legal advice, tax advice, or any other form of professional advisory service.

Lenders Home LLC is not a registered investment advisor, attorney, or tax professional. Before making any financial decisions, including entering into a loan agreement, you should consult with qualified financial, legal, and tax professionals who can evaluate your specific circumstances and provide personalized guidance.`,
  },
  {
    icon: '◑',
    title: 'Regulatory Compliance Statement',
    content: `Lenders Home LLC operates in compliance with all applicable federal and state laws and regulations governing lending services, including but not limited to the Truth in Lending Act (TILA), the Equal Credit Opportunity Act (ECOA), the Fair Housing Act (FHA), the Gramm-Leach-Bliley Act (GLBA), and applicable state consumer protection laws.

We are committed to fair lending practices and do not discriminate on the basis of race, color, religion, national origin, sex, marital status, age, familial status, disability, or any other characteristic protected by applicable law. If you believe you have been subject to discriminatory lending practices, you may file a complaint with the Consumer Financial Protection Bureau (CFPB) at consumerfinance.gov or your state's banking regulator.

NMLS information is available upon request. Licensing information varies by state and loan type.`,
  },
];

export default function DisclaimerPage() {
  return (
    <main className="min-h-screen bg-cloud">
      <Header />
      {/* Hero */}
      <section className="pt-28 pb-16 px-6 lg:px-10 bg-white border-b border-slate-mist/30">
        <div className="max-w-5xl mx-auto">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-sky-light text-sky-dark text-xs font-semibold font-body mb-6 tracking-wide uppercase">
            Legal
          </div>
          <h1 className="font-heading text-display font-bold text-slate-dark leading-tight mb-4">Disclaimer</h1>
          <p className="text-slate-fog font-body">Last Updated: June 1, 2026</p>
          <p className="text-slate-fog font-body mt-3 max-w-3xl leading-relaxed">
            Please read this Disclaimer carefully. By using the Lenders Home LLC platform, you acknowledge and agree to the terms set forth below. This Disclaimer is incorporated into and forms part of our Terms and Conditions.
          </p>
        </div>
      </section>
      {/* Disclaimer sections */}
      <section className="py-16 px-6 lg:px-10">
        <div className="max-w-4xl mx-auto space-y-6">
          {disclaimerItems?.map((item, i) => (
            <div key={i} className="bg-white rounded-3xl shadow-card border border-slate-mist/30 p-8">
              <div className="flex items-start gap-4 mb-4">
                <span className="text-sky text-2xl flex-shrink-0 mt-0.5">{item?.icon}</span>
                <h2 className="font-heading text-xl font-bold text-slate-dark">{item?.title}</h2>
              </div>
              <div className="text-sm text-slate-fog font-body leading-relaxed whitespace-pre-line pl-10">
                {item?.content}
              </div>
            </div>
          ))}

          {/* General notice */}
          <div className="bg-sky-light rounded-3xl p-8 border border-sky/20">
            <h3 className="font-heading text-lg font-bold text-slate-dark mb-3">General Notice</h3>
            <p className="text-sm text-slate-fog font-body leading-relaxed">
              Lenders Home LLC reserves the right to modify this Disclaimer at any time. Changes will be posted on this page with an updated effective date. Your continued use of the platform following any changes constitutes your acceptance of the revised Disclaimer. For questions about this Disclaimer, contact us at <strong className="text-slate">legal@lendershomellc.com</strong>.
            </p>
          </div>
        </div>
      </section>
      <Footer />
    </main>
  );
}
